import { Toast, useToast } from '@zillow/constellation-10';
import { useCallback, useContext } from 'react';
import { useCookies } from 'react-cookie';
import {
  useSatelliteWebLandlordApplicationHandler_inviteToApply,
  useSatelliteWebLandlordConversationHandler_addSystemMessage,
  useSatelliteWebLandlordConversationHandler_archiveConversation,
  useSatelliteWebLandlordConversationHandler_markConversationAsRead,
  useSatelliteWebLandlordConversationHandler_markConversationAsUnread,
  useSatelliteWebLandlordConversationHandler_reportConversation,
  useSatelliteWebLandlordConversationHandler_unarchiveConversation,
  useSatelliteWebLandlordConversationHandler_updateConversationAttributes,
} from '@zg-rentals/ts-satellite-client-react-query';
import {
  MultiBrandSupportUserReportConversationReasonType,
  SatelliteConversationFolderTypeV2,
  SatelliteConversationMessageType,
  SatelliteNextStepType,
} from '@zg-rentals/ts-satellite-client';
import { getCurrentSessionURL } from '../utils/fullStory';
import useModal from './useModal';
import { ModalType } from '../types/ModalType';
import { APPLICATIONS_UPSELL_MODAL_COOKIE } from '../components/Modals/ApplicationsUpsellModal';
import { LeadManagementContext } from '../contexts/LeadManagementContextProvider';
import { AppContext } from '../contexts/AppContextProvider';
import { leadManagementShowProfileRequestedForConversationIdStore } from '../lib/leadManagementStores';
import useDevices from './useDevices';
import useRestoreMessagingWindow from './useRestoreMessagingWindow';
import useQueryParamsNext from './useQueryParamsNext';
import type { OtherActionsTriggerObject } from '../constants/clickstreamEvents/otherActionsMenu';
import { emitClickOptionsFromOtherActionsMenuEvent } from '../constants/clickstreamEvents/otherActionsMenu';
import { emitViewMessagesFromRenterCardEvent } from '../constants/clickstreamEvents/renterDetailsModal';
import { reportReasonClickstreamEventMap } from '../constants/clickstreamEvents/reportProblemModal';
import { TRIGGER_LOCATION } from '../constants/clickstreamEvents/globals';
import { useAuth } from '../AuthProvider';
import {
  useCachedLeadByConversationId,
  useInvalidateLeadManagementTable,
  useOptimisticLeadRemoval,
  useOptimisticLeadUpdate,
} from './useLeadManagementTableQuery';
import { CONVERSATION_MESSAGES_TAG } from './useConversationMessagesQuery';

type UseLeadManagementActionsArgs = {
  conversationId: string;
};

type ReportConversationArgs = {
  reportReasonType: MultiBrandSupportUserReportConversationReasonType;
  userReportComment?: string;
};

export default ({ conversationId }: UseLeadManagementActionsArgs) => {
  const { user } = useAuth();
  const { enqueueToast } = useToast();
  const { clearModal, setActiveModal } = useModal();
  const [cookies] = useCookies([APPLICATIONS_UPSELL_MODAL_COOKIE]);
  const { isImpersonator } = useContext(AppContext);
  const fullStorySessionUrl = getCurrentSessionURL();
  const { conversationId: renterDetailsConversationId, folder } = useContext(LeadManagementContext);
  const { updateQueryParams } = useQueryParamsNext();
  const { isDesktopView } = useDevices();
  const restoreMessagingWindow = useRestoreMessagingWindow();
  const { mutateAsync: inviteToApply } = useSatelliteWebLandlordApplicationHandler_inviteToApply({
    meta: {
      context: {
        api: 'inviteToApply',
        details: 'useLeadManagementActions - invite renter to apply',
      },
    },
  });
  // Invalidate the (poll-less) getMessages cache too, so the open modal's status
  // (isArchived / isSpam, e.g. the disabled composer) doesn't go stale.
  const { mutateAsync: archiveConversationMutate } = useSatelliteWebLandlordConversationHandler_archiveConversation({
    meta: {
      invalidates: ['satellite:conversationCounts', CONVERSATION_MESSAGES_TAG],
      context: {
        api: 'archiveConversation',
        details: 'useLeadManagementActions - archiving conversation',
      },
    },
  });

  const { mutateAsync: unarchiveConversationMutate } = useSatelliteWebLandlordConversationHandler_unarchiveConversation(
    {
      meta: {
        invalidates: ['satellite:conversationCounts', CONVERSATION_MESSAGES_TAG],
        context: {
          api: 'unarchiveConversation',
          details: 'useLeadManagementActions - unarchiving conversation',
        },
      },
    },
  );

  const { mutateAsync: reportConversationMutate } = useSatelliteWebLandlordConversationHandler_reportConversation({
    meta: {
      invalidates: ['satellite:conversationCounts', CONVERSATION_MESSAGES_TAG],
      context: {
        api: 'reportConversation',
        details: 'useLeadManagementActions - reporting conversation',
      },
    },
  });
  const { mutate: markConversationAsUnread } = useSatelliteWebLandlordConversationHandler_markConversationAsUnread({
    meta: {
      invalidates: ['satellite:conversationCounts'],
      context: {
        api: 'markConversationAsUnread',
        details: 'useLeadManagementActions - marking conversation as unread',
      },
    },
  });
  const { mutateAsync: markConversationAsReadMutate } =
    useSatelliteWebLandlordConversationHandler_markConversationAsRead({
      meta: {
        invalidates: ['satellite:conversationCounts'],
        context: {
          api: 'markConversationAsRead',
          details: 'useLeadManagementActions - marking conversation as read',
        },
      },
    });
  const { mutateAsync: updateConversationAttributes } =
    useSatelliteWebLandlordConversationHandler_updateConversationAttributes({
      meta: {
        context: {
          api: 'updateConversationAttributes',
          details: 'useLeadManagementActions - Error marking lead as favorite or unfavorite',
        },
      },
    });

  const invalidateLeadManagementTable = useInvalidateLeadManagementTable();
  const optimisticLeadRemoval = useOptimisticLeadRemoval();
  const optimisticLeadUpdate = useOptimisticLeadUpdate();

  const { mutate: addSystemMessage, error: addSystemMessageError } =
    useSatelliteWebLandlordConversationHandler_addSystemMessage({
      meta: {
        context: {
          api: 'addSystemMessage',
          details: 'useLeadManagementActions - Error adding system message to conversation',
          fullStorySessionUrl,
        },
      },
    });

  const lead = useCachedLeadByConversationId(conversationId);
  const { leadId, renterName, renterRelayEmailAddress, renterPhoneNumber } = lead?.renterInfo || {};
  const { isApplicationsAccepted } = lead?.applicationInfo || {};
  const listingAlias = lead?.listingDetails?.listingAlias ?? '';

  const handleOptionsInOtherActionsClickstreamEvent = (
    optionName: string,
    triggerObjectName?: OtherActionsTriggerObject,
  ) => {
    emitClickOptionsFromOtherActionsMenuEvent({
      user,
      listing: { listingAlias },
      triggerSourceName: optionName,
      triggerObjectName,
      lead: { leadId },
    });
  };

  const handleViewMessageClick = () => {
    emitViewMessagesFromRenterCardEvent(user, { listingAlias }, { leadId });
  };

  const handleOpenRenterMessagingModal = () => {
    const isRenterDetailsDrawerOpen = !!renterDetailsConversationId;
    const shouldCloseDrawer = isRenterDetailsDrawerOpen && !isDesktopView;
    // Explicitly opening a conversation always presents the full window — un-minimized, with the
    // previously-minimized conversation's thread invalidated (a reopen within staleTime would
    // otherwise skip the refetch).
    restoreMessagingWindow();
    updateQueryParams({
      queryParamValues: {
        messagingConversationId: conversationId,
        ...(shouldCloseDrawer && { conversationId: null }),
      },
      shouldReplaceHistory: true,
    });
  };

  const handleArchiveClick = async (): Promise<void> => {
    if (isImpersonator) return;
    handleOptionsInOtherActionsClickstreamEvent('archive');

    const rollback = optimisticLeadRemoval(conversationId);

    try {
      await archiveConversationMutate({ postData: { conversationId } });

      enqueueToast(<Toast tone="success">Lead archived</Toast>);
      updateQueryParams({
        queryParamValues: { conversationId: null },
        shouldReplaceHistory: true,
      });
      clearModal();
      invalidateLeadManagementTable();
    } catch {
      rollback();
      enqueueToast(<Toast tone="critical">Lead couldn’t be archived. Try again.</Toast>);
    }
  };

  const handleReportClick = async ({ reportReasonType, userReportComment }: ReportConversationArgs): Promise<void> => {
    if (isImpersonator) return;

    const reportReasonTypeMappingFn =
      reportReasonClickstreamEventMap[
        reportReasonType as Exclude<
          MultiBrandSupportUserReportConversationReasonType,
          MultiBrandSupportUserReportConversationReasonType.NOT_REPORTED
        >
      ];

    if (reportReasonTypeMappingFn) {
      reportReasonTypeMappingFn({
        user,
        listing: { listingAlias },
        triggerLocation: TRIGGER_LOCATION.LeadManagementPage,
        lead: { leadId },
      });
    }

    const rollback = optimisticLeadRemoval(conversationId);

    try {
      await reportConversationMutate({
        postData: {
          conversationId,
          reportReasonType,
          reportUserComment: userReportComment,
        },
      });

      enqueueToast(<Toast tone="success">Lead reported</Toast>);
      updateQueryParams({
        queryParamValues: { conversationId: null },
        shouldReplaceHistory: true,
      });
      clearModal();
      invalidateLeadManagementTable();
    } catch {
      rollback();
      enqueueToast(<Toast tone="critical">Lead couldn’t be reported. Try again.</Toast>);
    }
  };

  const handleMoveToInboxClick = async (): Promise<void> => {
    if (isImpersonator) return;
    handleOptionsInOtherActionsClickstreamEvent('move_to_inbox');

    const rollback = optimisticLeadRemoval(conversationId);

    try {
      if (folder === SatelliteConversationFolderTypeV2.SPAM) {
        await reportConversationMutate({
          postData: {
            conversationId,
            reportReasonType: MultiBrandSupportUserReportConversationReasonType.NOT_REPORTED,
          },
        });
      } else {
        await unarchiveConversationMutate({ postData: { conversationId } });
      }

      enqueueToast(<Toast tone="success">Lead moved to inbox</Toast>);
      invalidateLeadManagementTable();
    } catch {
      rollback();
      enqueueToast(<Toast tone="critical">Lead couldn’t be moved to inbox. Try again.</Toast>);
    }
  };

  const handleMarkAsRead = async (lastReadMs: string): Promise<void> => {
    if (isImpersonator) return;
    handleOptionsInOtherActionsClickstreamEvent('mark_read');

    const rollback = optimisticLeadUpdate(conversationId, (lead) => ({
      ...lead,
      latestContact: { ...lead.latestContact, isRead: true },
    }));

    try {
      await markConversationAsReadMutate({
        postData: {
          conversationId,
          lastReadMs: Number(lastReadMs),
        },
      });

      invalidateLeadManagementTable();
    } catch {
      rollback();
      enqueueToast(<Toast tone="critical">Message couldn’t be marked as read. Try again.</Toast>);
    }
  };

  const handleMarkAsUnread = (): void => {
    if (isImpersonator) return;
    handleOptionsInOtherActionsClickstreamEvent('mark_unread');

    const rollback = optimisticLeadUpdate(conversationId, (lead) => ({
      ...lead,
      latestContact: { ...lead.latestContact, isRead: false },
    }));

    markConversationAsUnread(
      { postData: { conversationId } },
      {
        onSuccess: () => {
          invalidateLeadManagementTable();
        },
        onError: () => {
          rollback();
          enqueueToast(<Toast tone="critical">Message couldn’t be marked as unread. Try again.</Toast>);
        },
      },
    );
  };

  const handleCallClick = () => {
    handleOptionsInOtherActionsClickstreamEvent('call');
    if (!renterPhoneNumber) {
      return enqueueToast(<Toast tone="critical">No phone number found for lead</Toast>);
    }
    setActiveModal(ModalType.ContactInfoCallModal);
  };

  const handleInviteToApplyClick = async (isRetry = false): Promise<void> => {
    if (isImpersonator) return;

    const renterNames = renterName?.split(' ') || [];
    const renterFirstName = renterNames[0] || '';
    // Send empty string when name has no space; avoids duplicating the first name as last name
    const renterLastName = renterNames.length > 1 ? renterNames[renterNames.length - 1] : '';

    const postData = {
      listingAlias,
      renterFirstName,
      renterLastName,
      toEmail: renterRelayEmailAddress || '',
      resendEmail: true,
    };

    const rollback = optimisticLeadUpdate(conversationId, (lead) => ({
      ...lead,
      statusLabel: {
        ...lead.statusLabel,
        linkId: 'statusLabelInvitedToApply',
        text: 'Invited to apply',
      },
      // A suppressed (null) next step stays suppressed; the refetch decides what comes next.
      nextStep: lead.nextStep && {
        ...lead.nextStep,
        nextStepType: SatelliteNextStepType.LINK,
        link: {
          linkData: '',
          linkId: 'nextStepSendMessage',
          text: 'Message',
          url: `/rental-manager/inbox/${listingAlias}/${conversationId}`,
        },
      },
      latestContact: {
        ...lead.latestContact,
        hasAttachments: false,
        isMessageOwner: false,
        isNew: false,
        isRead: false,
        messageDateMs: Date.now(),
        messageText: `You sent ${renterName} an application invitation`,
        messageType: SatelliteConversationMessageType.APPLICATION_INVITATION_V2,
        notRepliedTo: false,
        senderName: 'Zillow',
      },
    }));

    try {
      await inviteToApply({ postData });

      enqueueToast(<Toast tone="success">Invite sent</Toast>);
      invalidateLeadManagementTable();

      // Allow landlords a chance to enable applications if it's not enabled
      // for their listing by showing them the Applications Upsell Modal.
      // The modal will only appear once.
      if (!isApplicationsAccepted && !cookies[APPLICATIONS_UPSELL_MODAL_COOKIE]) {
        setActiveModal(ModalType.ApplicationsUpsellModal);
      }

      // The Proxit leadManagementTable endpoint has a slight delay before reflecting
      // the invite status. Delay the refetch so the server has time to process it;
      // the optimistic update above keeps the UI responsive in the meantime.
      setTimeout(invalidateLeadManagementTable, 3000);
    } catch {
      rollback();

      if (!isRetry) {
        return handleInviteToApplyClick(true);
      }

      enqueueToast(<Toast tone="critical">Invitation couldn’t be sent. Try again.</Toast>);
    }
  };

  const handleRequestRenterProfile = async (onRequestRenterProfileCompleted?: () => void) => {
    if (isImpersonator) return;

    addSystemMessage(
      {
        postData: {
          conversationId: conversationId,
          messageType: SatelliteConversationMessageType.RENTER_PROFILE_REQUEST,
        },
      },
      {
        onSuccess: async () => {
          if (onRequestRenterProfileCompleted) {
            onRequestRenterProfileCompleted();
          }
          leadManagementShowProfileRequestedForConversationIdStore.set(conversationId);
          updateQueryParams({
            queryParamValues: {
              conversationId: null,
            },
            shouldReplaceHistory: true,
          });
          clearModal();
          invalidateLeadManagementTable();
        },
        onError: () => {
          enqueueToast(<Toast tone="critical">Request couldn’t be sent. Try again.</Toast>);
        },
      },
    );
  };

  const handleFavoriteUnfavoriteClick = async (isFavorite: boolean): Promise<void> => {
    if (isImpersonator) return;

    const rollback = optimisticLeadUpdate(conversationId, (lead) => ({
      ...lead,
      status: { ...lead.status, isFavorite: !isFavorite },
    }));

    try {
      await updateConversationAttributes({
        postData: {
          conversationId,
          isFavorite: !isFavorite,
        },
      });

      invalidateLeadManagementTable();
    } catch {
      rollback();
      const favoriteOrUnfavorite = isFavorite ? 'unfavorite' : 'favorite';
      const errorMessage = `Lead couldn’t be marked as ${favoriteOrUnfavorite}. Try again.`;
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  const onAddMessageCompletedCacheUpdate = useCallback(
    ({ newMessage }: { newMessage: { messageId?: string | number | null }; [key: string]: unknown }) => {
      if (newMessage?.messageId?.toString().includes('-')) {
        return;
      }
      invalidateLeadManagementTable();
    },
    [invalidateLeadManagementTable],
  );

  return {
    handleArchiveClick,
    handleCallClick,
    handleInviteToApplyClick,
    handleMarkAsRead,
    handleMarkAsUnread,
    handleMoveToInboxClick,
    handleReportClick,
    handleRequestRenterProfile,
    handleOpenRenterMessagingModal,
    handleOptionsInOtherActionsClickstreamEvent,
    handleViewMessageClick,
    handleFavoriteUnfavoriteClick,
    onAddMessageCompletedCacheUpdate,
    hasErrorOnRequestRenterProfile: Boolean(addSystemMessageError),
  };
};
